import { createClient } from '@supabase/supabase-js';
import config from '../config';
import { SubscriptionTier } from '../types';

const { url: supabaseUrl, anonKey: supabaseAnonKey } = config.supabase;

// Extract project ref from Supabase URL for migration purposes
const projectRef = supabaseUrl.match(/https:\/\/([^.]+)\.supabase/)?.[1] || '';
const defaultStorageKey = `sb-${projectRef}-auth-token`;

// Migrate session from legacy custom key to default Supabase key
// This ensures users logged in with old key format don't get logged out
function migrateAuthStorage(): void {
  const legacyKey = 'complydeck-auth';
  try {
    const legacySession = localStorage.getItem(legacyKey);
    const existingSession = localStorage.getItem(defaultStorageKey);

    if (legacySession && !existingSession) {
      // Copy to new key format
      localStorage.setItem(defaultStorageKey, legacySession);
      console.log('[Auth] Migrated session from legacy storage key');
    }
    // Remove legacy key after migration (whether we migrated or not)
    if (legacySession) {
      localStorage.removeItem(legacyKey);
    }
  } catch (e) {
    console.error('[Auth] Error migrating auth storage:', e);
  }
}

// Run migration before creating the client
migrateAuthStorage();

// Use default Supabase storage key (sb-{project-ref}-auth-token) for better
// cross-tab session persistence. Previously used custom 'complydeck-auth' key
// which caused issues with session not being recognized across browser tabs.
export const supabase = createClient(supabaseUrl, supabaseAnonKey, {
  auth: {
    persistSession: true,
    autoRefreshToken: true,
    detectSessionInUrl: true,
    storage: window.localStorage, // Explicitly use localStorage for persistence
  },
});

// Edge Functions URL - configured in config/index.ts
// Production: routes through API proxy (api.complydeck.com/functions/v1)
// Development: calls Supabase directly (local backend doesn't proxy functions)
const FUNCTIONS_URL = config.functionsUrl;

// Helper to get auth headers (uses session token if available, otherwise anon key)
async function getAuthHeaders(): Promise<HeadersInit> {
  const { data: { session } } = await supabase.auth.getSession();
  return {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${session?.access_token || supabaseAnonKey}`,
  };
}

// Helper for public endpoints (no auth required, but needs anon key for Edge Function access)
function getPublicHeaders(): HeadersInit {
  return {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${supabaseAnonKey}`,
  };
}

// Helper to safely parse JSON response, handling HTML errors
async function safeParseResponse<T>(response: Response, fallbackError: string): Promise<T> {
  const text = await response.text();

  // Check if response is HTML (common error when Edge Function fails)
  if (text.trim().startsWith('<!') || text.trim().startsWith('<html')) {
    console.error('Received HTML instead of JSON:', text.substring(0, 200));
    throw new Error(`${fallbackError}: Server returned HTML instead of JSON. The service may be unavailable.`);
  }

  try {
    return JSON.parse(text);
  } catch (e) {
    console.error('Failed to parse JSON response:', text.substring(0, 200));
    throw new Error(`${fallbackError}: Invalid response from server`);
  }
}

// Validate email for signup eligibility
export async function validateSignup(email: string): Promise<{
  allowed: boolean;
  reason?: string;
  message: string;
  project_exists?: boolean;
  has_invite?: boolean;
  is_first_user?: boolean;
  project?: { id: string; company_name: string };
  domain?: string;
}> {
  const response = await fetch(`${FUNCTIONS_URL}/validate-signup`, {
    method: 'POST',
    headers: getPublicHeaders(),
    body: JSON.stringify({ email }),
  });
  if (!response.ok) {
    const error = await safeParseResponse<{ error?: string }>(response, 'Failed to validate email');
    throw new Error(error.error || 'Failed to validate email');
  }
  return safeParseResponse(response, 'Failed to validate email');
}

// Complete signup - creates profile and handles invite acceptance
export async function completeSignup(fullName: string): Promise<{
  status: string;
  project?: { id: string; company_name: string; domain: string };
  role?: string;
  needs_setup: boolean;
  domain?: string;
  message?: string;
}> {
  const headers = await getAuthHeaders();
  const response = await fetch(`${FUNCTIONS_URL}/complete-signup`, {
    method: 'POST',
    headers,
    body: JSON.stringify({ full_name: fullName }),
  });
  if (!response.ok) {
    const error = await safeParseResponse<{ error?: string }>(response, 'Failed to complete signup');
    throw new Error(error.error || 'Failed to complete signup');
  }
  return safeParseResponse(response, 'Failed to complete signup');
}

// Setup project for first-time admin
export async function setupProject(data: {
  company_name: string;
  industry?: string;
  description?: string;
}): Promise<{
  status: string;
  project: { id: string; domain: string; company_name: string };
  role: string;
  message: string;
}> {
  const headers = await getAuthHeaders();
  const response = await fetch(`${FUNCTIONS_URL}/setup-project`, {
    method: 'POST',
    headers,
    body: JSON.stringify(data),
  });
  if (!response.ok) {
    const error = await safeParseResponse<{ error?: string }>(response, 'Failed to setup project');
    throw new Error(error.error || 'Failed to setup project');
  }
  return safeParseResponse(response, 'Failed to setup project');
}

// Get user context (profile, project, role)
export async function getUserContext(): Promise<{
  user: { id: string; email: string };
  profile: { id: string; email: string; full_name: string } | null;
  membership: { id: string; role: string; joined_at: string } | null;
  project: {
    id: string;
    domain: string;
    company_name: string;
    industry?: string;
    settings?: any;
    member_count?: number;
    project_slug?: string;
    description?: string;
    response_verbosity?: string;
    qa_url_handling?: string;
    always_include_policy_context?: boolean;
    subscription_tier?: SubscriptionTier;
  } | null;
  needs_profile: boolean;
  needs_project_setup: boolean;
}> {
  const headers = await getAuthHeaders();
  const response = await fetch(`${FUNCTIONS_URL}/get-user-context`, {
    method: 'GET',
    headers,
  });
  if (!response.ok) {
    const error = await safeParseResponse<{ error?: string }>(response, 'Failed to get user context');
    throw new Error(error.error || 'Failed to get user context');
  }
  return safeParseResponse(response, 'Failed to get user context');
}

// Create invite (admin only)
export async function createInvite(email: string, role: 'admin' | 'member' = 'member'): Promise<{
  status: string;
  invite: { id: string; email: string; role: string; expires_at: string };
  project: { id: string; company_name: string };
  message: string;
}> {
  const headers = await getAuthHeaders();
  const response = await fetch(`${FUNCTIONS_URL}/create-invite`, {
    method: 'POST',
    headers,
    body: JSON.stringify({ email, role }),
  });
  if (!response.ok) {
    const error = await safeParseResponse<{ error?: string }>(response, 'Failed to create invite');
    throw new Error(error.error || 'Failed to create invite');
  }
  return safeParseResponse(response, 'Failed to create invite');
}

// List invites (admin only)
export async function listInvites(): Promise<{
  invites: Array<{
    id: string;
    email: string;
    role: string;
    status: string;
    created_at: string;
    expires_at: string;
    accepted_at?: string;
  }>;
  count: number;
}> {
  const headers = await getAuthHeaders();
  const response = await fetch(`${FUNCTIONS_URL}/list-invites`, {
    method: 'GET',
    headers,
  });
  if (!response.ok) {
    const error = await safeParseResponse<{ error?: string }>(response, 'Failed to list invites');
    throw new Error(error.error || 'Failed to list invites');
  }
  return safeParseResponse(response, 'Failed to list invites');
}

// Cancel invite (admin only)
export async function cancelInvite(inviteId: string): Promise<{ status: string; message: string }> {
  const headers = await getAuthHeaders();
  const response = await fetch(`${FUNCTIONS_URL}/cancel-invite`, {
    method: 'POST',
    headers,
    body: JSON.stringify({ invite_id: inviteId }),
  });
  if (!response.ok) {
    const error = await safeParseResponse<{ error?: string }>(response, 'Failed to cancel invite');
    throw new Error(error.error || 'Failed to cancel invite');
  }
  return safeParseResponse(response, 'Failed to cancel invite');
}

// Project settings types
export interface SupabaseProjectSettings {
  project_id: string;
  company_name: string;
  description: string | null;
  industry?: string | null;
  response_verbosity: string;
  qa_url_handling: string;
  always_include_policy_context: boolean;
  subscription_tier?: SubscriptionTier;
  // BYOK fields (only present for admins)
  byok_enabled?: boolean;
  has_api_key?: boolean;
}

// Get project settings from Supabase
export async function getProjectSettingsFromSupabase(projectSlug: string): Promise<SupabaseProjectSettings> {
  const headers = await getAuthHeaders();
  const response = await fetch(`${FUNCTIONS_URL}/get-project-settings?project_slug=${encodeURIComponent(projectSlug)}`, {
    method: 'GET',
    headers,
  });
  if (!response.ok) {
    const error = await safeParseResponse<{ error?: string }>(response, 'Failed to get project settings');
    throw new Error(error.error || 'Failed to get project settings');
  }
  return safeParseResponse(response, 'Failed to get project settings');
}

// Update project settings in Supabase
export async function updateProjectSettingsInSupabase(
  projectSlug: string,
  settings: {
    company_name?: string;
    description?: string | null;
    response_verbosity?: string;
    always_include_policy_context?: boolean;
  }
): Promise<SupabaseProjectSettings> {
  const headers = await getAuthHeaders();
  const response = await fetch(`${FUNCTIONS_URL}/update-project-settings`, {
    method: 'POST',
    headers,
    body: JSON.stringify({ project_slug: projectSlug, ...settings }),
  });
  if (!response.ok) {
    const error = await safeParseResponse<{ error?: string }>(response, 'Failed to update project settings');
    throw new Error(error.error || 'Failed to update project settings');
  }
  return safeParseResponse(response, 'Failed to update project settings');
}

// BYOK Settings Types
export interface BYOKSettings {
  byok_enabled: boolean;
  has_api_key: boolean;
}

// Update BYOK settings (uses the same update-project-settings endpoint)
export async function updateBYOKSettingsInSupabase(
  projectSlug: string,
  settings: {
    byok_enabled: boolean;
    gemini_api_key?: string;
  }
): Promise<BYOKSettings> {
  const headers = await getAuthHeaders();
  const response = await fetch(`${FUNCTIONS_URL}/update-project-settings`, {
    method: 'POST',
    headers,
    body: JSON.stringify({
      project_slug: projectSlug,
      byok_enabled: settings.byok_enabled,
      gemini_api_key: settings.gemini_api_key
    }),
  });
  if (!response.ok) {
    const error = await safeParseResponse<{ error?: string }>(response, 'Failed to update API key settings');
    throw new Error(error.error || 'Failed to update API key settings');
  }
  const result = await safeParseResponse<SupabaseProjectSettings>(response, 'Failed to update settings');
  return {
    byok_enabled: result.byok_enabled || false,
    has_api_key: result.has_api_key || false,
  };
}

// =============================================================================
// SLACK INTEGRATION
// =============================================================================

export interface SlackIntegrationStatus {
  connected: boolean;
  is_active?: boolean;
  team_name?: string;
  team_id?: string;
  installed_at?: string;
  can_manage: boolean;
}

// Get Slack integration status for current user's project
export async function getSlackStatus(): Promise<SlackIntegrationStatus> {
  const headers = await getAuthHeaders();
  const response = await fetch(`${FUNCTIONS_URL}/slack-status`, {
    method: 'GET',
    headers,
  });
  if (!response.ok) {
    const error = await safeParseResponse<{ error?: string }>(response, 'Failed to get Slack status');
    throw new Error(error.error || 'Failed to get Slack status');
  }
  return safeParseResponse(response, 'Failed to get Slack status');
}

// Start Slack OAuth flow - returns URL to redirect user to
export async function startSlackOAuth(): Promise<{ oauth_url: string; message: string }> {
  const headers = await getAuthHeaders();
  const response = await fetch(`${FUNCTIONS_URL}/slack-oauth-start`, {
    method: 'POST',
    headers,
  });
  if (!response.ok) {
    const error = await safeParseResponse<{ error?: string }>(response, 'Failed to start Slack connection');
    throw new Error(error.error || 'Failed to start Slack connection');
  }
  return safeParseResponse(response, 'Failed to start Slack connection');
}

// Disconnect Slack integration
export async function disconnectSlack(): Promise<{ status: string; message: string }> {
  const headers = await getAuthHeaders();
  const response = await fetch(`${FUNCTIONS_URL}/slack-disconnect`, {
    method: 'POST',
    headers,
  });
  if (!response.ok) {
    const error = await safeParseResponse<{ error?: string }>(response, 'Failed to disconnect Slack');
    throw new Error(error.error || 'Failed to disconnect Slack');
  }
  return safeParseResponse(response, 'Failed to disconnect Slack');
}
